/**
 * Attribution Collector
 *
 * Gathers all available attribution context from storage, cookies,
 * and the current URL. Call `collectAttribution()` at form-submission
 * time to build the metadata payload sent to the backend.
 */

import type {
  AttributionMetadata,
  ClickIds,
  EmailCampaignIds,
  UtmParams,
} from './types';
import { LEGACY_VISITOR_ID_COOKIE, STORAGE_KEYS, VISITOR_ID_COOKIE } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Extract the GA4 client_id from the `_ga` cookie.
 * The cookie value looks like `GA1.1.1234567890.1234567890` — the client_id
 * is the last two dot-separated segments joined by a dot.
 */
function getGAClientId(): string | undefined {
  const ga = getCookie('_ga');
  if (!ga) return undefined;
  const parts = ga.split('.');
  if (parts.length >= 4) {
    return `${parts[2]}.${parts[3]}`;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Collect all available attribution metadata.
 *
 * Safe to call from any client component — returns an empty object on
 * the server or when storage is unavailable.
 */
export function collectAttribution(userId?: string): AttributionMetadata {
  if (typeof window === 'undefined') return {};

  const firstTouch = readJson<UtmParams>(localStorage, STORAGE_KEYS.UTM_FIRST_TOUCH);
  const lastTouch = readJson<UtmParams>(sessionStorage, STORAGE_KEYS.UTM_LAST_TOUCH);
  const clickIds = readJson<ClickIds>(sessionStorage, STORAGE_KEYS.CLICK_IDS);
  const emailCampaign = readJson<EmailCampaignIds>(sessionStorage, STORAGE_KEYS.EMAIL_CAMPAIGN);
  const landingPage = sessionStorage.getItem(STORAGE_KEYS.LANDING_PAGE) ?? undefined;
  const referrer = sessionStorage.getItem(STORAGE_KEYS.REFERRER) ?? undefined;

  const visitorId = getCookie(VISITOR_ID_COOKIE) ?? getCookie(LEGACY_VISITOR_ID_COOKIE);
  const gaClientId = getGAClientId();

  const metadata: AttributionMetadata = {
    ...(firstTouch && { firstTouch }),
    ...(lastTouch && { lastTouch }),
    ...(clickIds && { clickIds }),
    ...(emailCampaign && { emailCampaign }),
    ...(visitorId && { visitorId }),
    ...(userId && { userId }),
    ...(gaClientId && { gaClientId }),
    // Snake-case aliases consumed by the Route Royal backend identity
    // resolution (form-submission / enquiry listeners read `rr_id` and
    // `ga_client_id`). Emitted alongside the camelCase keys for compatibility.
    ...(visitorId && { rr_id: visitorId }),
    ...(gaClientId && { ga_client_id: gaClientId }),
    conversionUrl: window.location.href,
    ...(landingPage && { landingPage }),
    ...(referrer && { referrer }),
    timestamp: new Date().toISOString(),
  };

  return metadata;
}

/**
 * Convenience: return a flat object of the most recent UTM + click ID values
 * (for backwards-compat with existing `getStoredUtmData()` callers).
 */
export function getStoredUtmData(): UtmParams & ClickIds {
  if (typeof window === 'undefined') return {};
  const utm = readJson<UtmParams>(sessionStorage, STORAGE_KEYS.UTM_LAST_TOUCH) ?? {};
  const clicks = readJson<ClickIds>(sessionStorage, STORAGE_KEYS.CLICK_IDS) ?? {};
  return { ...utm, ...clicks };
}
